import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// Local type definitions (adapted from Next.js version)
type DateRange = { from: Date; to: Date };
type StatGranularity = 'daily' | 'weekly' | 'monthly';

interface UrlSyncState {
  selectedDates: DateRange;
  selectedGranularity: StatGranularity;
  selectedStores: string[];
  selectedTimezone: string;
  selectedCurrency: string;
}

// Hook for managing URL synchronization
// Adapted from Next.js to React Router - uses navigate() instead of window.history.pushState()
export function useUrlSync(state: UrlSyncState) {
  const navigate = useNavigate();
  const debounceRef = useRef<NodeJS.Timeout>();

  // Update URL with current state (debounced to prevent rapid successive updates)
  const updateURL = useCallback((newState: Partial<UrlSyncState>) => {
    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce URL updates by 100ms
    debounceRef.current = setTimeout(() => {
      const finalState = {
        ...state,
        ...newState,
      };

      const params = new URLSearchParams();
      params.set('from', finalState.selectedDates.from.toISOString());
      params.set('to', finalState.selectedDates.to.toISOString());
      params.set('granularity', finalState.selectedGranularity);
      params.set('timezone', finalState.selectedTimezone);
      params.set('currency', finalState.selectedCurrency);

      // Only add stores to URL if there are any selected
      if (finalState.selectedStores.length > 0) {
        params.set('stores', finalState.selectedStores.join(','));
      }

      // Use React Router navigate with replace: true to update URL without adding to history
      navigate({ search: params.toString() }, { replace: true });
    }, 100);
  }, [navigate, state]);

  return { updateURL };
}
